import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classNames from 'classnames';
import isObject from 'lodash-es/isObject';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import Actions from '@panneau/action-actions';
import { usePanneauResource, useListsComponents } from '@panneau/core/contexts';
import { useResourceUrlGenerator } from '@panneau/core/hooks';
import { getComponentFromName } from '@panneau/core/utils';
import { useResourceItems } from '@panneau/data';
import Pagination from '@panneau/element-pagination';
import Filters from '@panneau/filter-filters';

var _excluded = ["component", "showPagination", "filters", "actions", "batchActions", "actionsProps"],
  _excluded2 = ["page"];
var propTypes = {
  resource: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.shape({})]).isRequired,
  query: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  paginated: PropTypes.bool,
  baseUrl: PropTypes.string,
  component: PropTypes.oneOfType([PropTypes.elementType, PropTypes.string]),
  componentProps: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  onQueryChange: PropTypes.func,
  onQueryReset: PropTypes.func,
  onPageChange: PropTypes.func,
  showFilters: PropTypes.bool,
  showActions: PropTypes.bool,
  selectable: PropTypes.bool,
  selectedItems: PropTypes.arrayOf(PropTypes.shape({})),
  onSelectionChange: PropTypes.func,
  multipleSelection: PropTypes.bool,
  listProps: PropTypes.shape({}),
  theme: PropTypes.string,
  className: PropTypes.string
};
var defaultProps = {
  query: null,
  paginated: true,
  component: null,
  baseUrl: null,
  componentProps: null,
  onQueryChange: null,
  onQueryReset: null,
  onPageChange: null,
  showFilters: true,
  showActions: true,
  selectable: false,
  selectedItems: null,
  onSelectionChange: null,
  multipleSelection: false,
  listProps: null,
  theme: null,
  className: null
};
var ResourceItemsList = function ResourceItemsList(_ref) {
  var providedResource = _ref.resource,
    query = _ref.query,
    baseUrl = _ref.baseUrl,
    onQueryChange = _ref.onQueryChange,
    onQueryReset = _ref.onQueryReset,
    onPageChange = _ref.onPageChange,
    paginated = _ref.paginated,
    showFilters = _ref.showFilters,
    showActions = _ref.showActions,
    selectable = _ref.selectable,
    initialSelectedItems = _ref.selectedItems,
    parentOnChangeSelection = _ref.onSelectionChange,
    multipleSelection = _ref.multipleSelection,
    customListProps = _ref.listProps,
    theme = _ref.theme,
    className = _ref.className;
  var panneauResource = usePanneauResource(providedResource);
  var resource = isObject(providedResource) ? providedResource : panneauResource;
  var _resource$id = resource.id,
    resourceId = _resource$id === void 0 ? null : _resource$id,
    _resource$index = resource.index,
    _resource$index2 = _resource$index === void 0 ? {} : _resource$index,
    _resource$index2$comp = _resource$index2.component,
    listComponent = _resource$index2$comp === void 0 ? null : _resource$index2$comp,
    _resource$index2$show = _resource$index2.showPagination,
    showPagination = _resource$index2$show === void 0 ? true : _resource$index2$show,
    _resource$index2$filt = _resource$index2.filters,
    filters = _resource$index2$filt === void 0 ? null : _resource$index2$filt,
    _resource$index2$acti = _resource$index2.actions,
    actions = _resource$index2$acti === void 0 ? null : _resource$index2$acti,
    _resource$index2$batc = _resource$index2.batchActions,
    batchActions = _resource$index2$batc === void 0 ? null : _resource$index2$batc,
    _resource$index2$acti2 = _resource$index2.actionsProps,
    actionsProps = _resource$index2$acti2 === void 0 ? null : _resource$index2$acti2,
    listProps = _objectWithoutProperties(_resource$index2, _excluded);
  var resourceUrlGenerator = useResourceUrlGenerator(isObject(providedResource) ? resourceId : providedResource);
  var _useMemo = useMemo(function () {
      var _ref2 = query || {},
        _ref2$page = _ref2.page,
        currentPage = _ref2$page === void 0 ? 1 : _ref2$page,
        rest = _objectWithoutProperties(_ref2, _excluded2);
      return [currentPage, rest];
    }, [query]),
    _useMemo2 = _slicedToArray(_useMemo, 2),
    page = _useMemo2[0],
    queryWithoutPage = _useMemo2[1];
  var _useResourceItems = useResourceItems(resource, queryWithoutPage, paginated ? parseInt(page, 10) : null),
    _useResourceItems$ite = _useResourceItems.items,
    items = _useResourceItems$ite === void 0 ? [] : _useResourceItems$ite,
    _useResourceItems$loa = _useResourceItems.loaded,
    loaded = _useResourceItems$loa === void 0 ? false : _useResourceItems$loa,
    _useResourceItems$loa2 = _useResourceItems.loading,
    loading = _useResourceItems$loa2 === void 0 ? false : _useResourceItems$loa2,
    _useResourceItems$pag = _useResourceItems.pagination,
    pagination = _useResourceItems$pag === void 0 ? null : _useResourceItems$pag,
    _useResourceItems$upd = _useResourceItems.updateItem,
    updateItem = _useResourceItems$upd === void 0 ? null : _useResourceItems$upd,
    _useResourceItems$rel = _useResourceItems.reload,
    reload = _useResourceItems$rel === void 0 ? null : _useResourceItems$rel,
    _useResourceItems$pag2 = _useResourceItems.pages,
    pages = _useResourceItems$pag2 === void 0 ? null : _useResourceItems$pag2;
  var _ref3 = pagination || {},
    _ref3$lastPage = _ref3.lastPage,
    lastPage = _ref3$lastPage === void 0 ? 0 : _ref3$lastPage,
    _ref3$total = _ref3.total,
    total = _ref3$total === void 0 ? 0 : _ref3$total;
  var ListComponents = useListsComponents();
  var ListComponent = getComponentFromName(listComponent || 'table', ListComponents);
  var withFilters = showFilters && filters !== null;
  var withActions = showActions && batchActions !== null && batchActions.length > 0;
  var finalSelectable = selectable || withActions;
  var withMultipleActions = withActions && batchActions.reduce(function (acc, it) {
    var _ref4 = it || {},
      _ref4$multiple = _ref4.multiple,
      multiple = _ref4$multiple === void 0 ? false : _ref4$multiple;
    if (multiple) {
      return true;
    }
    return acc;
  }, false);
  var finalMultipleSelection = withMultipleActions || multipleSelection;
  var _useState = useState(initialSelectedItems || null),
    _useState2 = _slicedToArray(_useState, 2),
    selectedItems = _useState2[0],
    setSelectedItems = _useState2[1];
  var onSelectionChange = useCallback(function (newSelection) {
    setSelectedItems(newSelection);
  }, [setSelectedItems]);
  useEffect(function () {
    if (parentOnChangeSelection !== null) {
      parentOnChangeSelection(selectedItems);
    }
  }, [selectedItems, parentOnChangeSelection]);
  var onActionsChange = useCallback(function () {
    if (reload !== null) {
      reload();
    }
  }, [reload]);
  var clearSelectedItems = useCallback(function () {
    setSelectedItems([]);
  }, [setSelectedItems]);
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, withFilters ? /*#__PURE__*/React.createElement(Filters, {
    filters: filters,
    value: query,
    onChange: onQueryChange,
    onReset: onQueryReset,
    theme: theme
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: classNames(['d-flex', 'flex-column', 'flex-md-row', 'align-items-start', {
      'justify-content-between': withActions,
      'justify-content-end': !withActions
    }])
  }, withActions ? /*#__PURE__*/React.createElement(Actions, {
    className: "mt-1 mb-2",
    actions: batchActions,
    value: selectedItems,
    onChange: onActionsChange,
    onConfirmed: clearSelectedItems,
    withConfirmation: true,
    theme: theme
  }) : null, paginated && showPagination ? /*#__PURE__*/React.createElement(Pagination, {
    page: page,
    lastPage: lastPage,
    total: total,
    url: baseUrl,
    query: query,
    onClickPage: onPageChange,
    className: "mt-1 mb-3",
    theme: theme,
    loading: loading && pages !== null,
    loaded: loaded,
    withPreviousNext: true,
    selectable: finalSelectable,
    selectedItems: selectedItems,
    onSelectionChange: onSelectionChange,
    multipleSelection: finalMultipleSelection
  }) : null), ListComponent !== null ? /*#__PURE__*/React.createElement(ListComponent, Object.assign({}, listProps, {
    items: items,
    actionsProps: _objectSpread({
      resource: resource,
      actions: actions,
      reload: reload,
      updateItem: updateItem,
      urlGenerator: resourceUrlGenerator
    }, actionsProps),
    selectable: finalSelectable,
    selectedItems: selectedItems,
    onSelectionChange: onSelectionChange,
    multipleSelection: finalMultipleSelection,
    resource: resource,
    baseUrl: baseUrl,
    theme: theme,
    query: query,
    onQueryChange: onQueryChange,
    onQueryReset: onQueryReset,
    loading: loading,
    loaded: loaded,
    emptyLabel: /*#__PURE__*/React.createElement("p", {
      className: "my-2"
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "WgIC2T",
      defaultMessage: [{
        "type": 0,
        "value": "No results found"
      }]
    }))
  }, customListProps)) : null, paginated && showPagination ? /*#__PURE__*/React.createElement(Pagination, {
    page: page,
    lastPage: lastPage,
    total: total,
    url: baseUrl,
    query: query,
    onClickPage: onPageChange,
    className: "mt-4 mb-1",
    theme: theme,
    loading: loading && pages !== null,
    loaded: loaded,
    withPreviousNext: true,
    selectable: finalSelectable,
    selectedItems: selectedItems,
    onSelectionChange: onSelectionChange,
    multipleSelection: finalMultipleSelection
  }) : null);
};
ResourceItemsList.propTypes = propTypes;
ResourceItemsList.defaultProps = defaultProps;

export { ResourceItemsList as default };
